import React, { useState } from 'react';
// import { Download, Calculator, Zap } from 'lucide-react';
import html2pdf from 'html2pdf.js';
const UVCalculatorApp = () => {
    const [activeCalculator, setActiveCalculator] = useState('duct');

    // Duct Calculator State
    const [ductData, setDuctData] = useState({
        clientName: '',
        uvSize: '',
        width: '',
        length: '',
        cfm: '',
        lampLength: '',
        numRows: '',
        numCols: '',
        totalWattage: '',
        at1mWcm2Input: '',
        at450mWcm2Input: ''
    });

    const [ductCalculations, setDuctCalculations] = useState({
        ductAreaMm2: 0,
        ductAreaM2: 0,
        m3Hr: 0,
        m3Min: 0,
        m3Sec: 0,
        flowVelocity: 0,
        exposureTime: 0,
        at1mWcm2: 0,
        at1Wcm2: 0,
        at1mIReqD90Wm2: 0,
        at1mNoOfLampsD90: 0,
        at1mIReqD99Wm2: 0,
        at1mNoOfLampsD99: 0,
        at450mWcm2: 0,
        at450mmWm2: 0,
        iReqD90Wm2: 0,
        noOfLampsD90: 0,
        iReqD99Wm2: 0,
        noOfLampsD99: 0,
        correctedUVD: 0,
        minimumIntAtD: 0,
        intensityAtA: 0,
        intensityAtB: 0
    });

    // AHU Calculator State
    const [ahuData, setAhuData] = useState({
        clientName: '',
        coilWMm: '',
        coilHMm: '',
        uvLampLMm: '',
        distance: '',
        airflowVolCfm: '',
        numRows: '',
        numCols: '',
        lampCount: '',
        totalWattage: '',
        rowWattage: '',
        intensityAt1m: '',
        uvWattageW: '',
        noOfLamps: '',
        distBtwnLamp: ''
    });

    const [ahuCalculations, setAhuCalculations] = useState({
        uvLampLInch: 0,
        uvLampLCm: 0,
        t2LA: 0,
        eWcm2: 0,
        eMWcm2: 0,
        exposureTimeTs: 0,
        doseEt: 0,
        totalIntensityMWcm2: 0,
        doseJm2: 0,
        noOfLamps: 0,
        correctedUVD: 0,
        correctedUVDx001: 0,
        minimumInt: 0,
        maximumInt: 0,
        avgInt: 0
    });

    // Helper functions for conversions
    const mmToCm = (mm) => parseFloat(mm) / 10;
    const cmToM = (cm) => parseFloat(cm) * 0.01;
    const mmToInch = (mm) => parseFloat(mm) / 25.4;

    // Duct Calculator Functions
    const handleDuctCalculation = () => {
        const width = parseFloat(ductData.width);
        const length = parseFloat(ductData.length);
        const cfm = parseFloat(ductData.cfm);
        const numRows = parseFloat(ductData.numRows);
        const numCols = parseFloat(ductData.numCols);
        const totalWattage = parseFloat(ductData.totalWattage);
        const lampLength = parseFloat(ductData.lampLength);
        const at1mWcm2Input = parseFloat(ductData.at1mWcm2Input);
        const at450mWcm2Input = parseFloat(ductData.at450mWcm2Input);

        // Basic calculations
        const ductAreaMm2 = width * length;
        const ductAreaM2 = ductAreaMm2 / 1000000;
        const m3Hr = cfm * 1.69901082;
        const m3Min = cfm * 0.0283168;
        const m3Sec = cfm * 0.000471947;
        const flowVelocity = m3Sec / ductAreaM2;
        const exposureTime = 2 / flowVelocity;

        // Intensity calculations
        const at1mWcm2 = at1mWcm2Input;
        const at1Wcm2 = at1mWcm2Input * 10;
        const at1mIReqD90Wm2 = 6 / exposureTime;
        const at1mNoOfLampsD90 = at1mIReqD90Wm2 / at1Wcm2;
        const at1mIReqD99Wm2 = 12 / exposureTime;
        const at1mNoOfLampsD99 = at1mIReqD99Wm2 / at1Wcm2;

        const at450mWcm2 = at450mWcm2Input;
        const at450mmWm2 = at450mWcm2Input * 10;
        const iReqD90Wm2 = 6 / exposureTime;
        const noOfLampsD90 = iReqD90Wm2 / at450mmWm2;
        const iReqD99Wm2 = 12 / exposureTime;
        const noOfLampsD99 = iReqD99Wm2 / at450mmWm2;

        // Complex calculations for final results
        const lengthInCm = length / 10;
        const widthInCm = width / 10;
        const lampLengthInCm = lampLength / 10;

        const ductWidthAlt = widthInCm / (numCols * 2);
        const ductHeightAlt = lengthInCm / (numRows * 2);
        const avgIntensityDistance = Math.max(ductHeightAlt, ductWidthAlt) / 1.5;
        const totalAverageIntensity = ((totalWattage * 0.3) * (Math.log(avgIntensityDistance) + 0.577) * (10 ** 6)) / (2 * Math.PI * lampLengthInCm * avgIntensityDistance);
        const contactTime = ((((lampLengthInCm / 30.48) + 1) * (lengthInCm / 30.48) * (widthInCm / 30.48)) / cfm) * 60;
        const correctedDosage = totalAverageIntensity * contactTime * 0.7;

        const diagonalDistance = Math.sqrt(ductHeightAlt ** 2 + ductWidthAlt ** 2);
        const minIntensity = (((totalWattage / (numCols * numRows)) * 0.3 * 10 ** 6) / (2 * Math.PI * lampLengthInCm * diagonalDistance)) * 0.63;
        const aIntensity = (((totalWattage / (numCols * numRows)) * 0.3 * 10 ** 6) / (2 * Math.PI * lampLengthInCm * ductWidthAlt)) * 0.63;
        const bIntensity = (((totalWattage / (numCols * numRows)) * 0.3 * 10 ** 6) / (2 * Math.PI * lampLengthInCm * ductHeightAlt)) * 0.63;

        setDuctCalculations({
            ductAreaMm2: parseFloat(ductAreaMm2.toFixed(4)),
            ductAreaM2: parseFloat(ductAreaM2.toFixed(4)),
            m3Hr: parseFloat(m3Hr.toFixed(4)),
            m3Min: parseFloat(m3Min.toFixed(4)),
            m3Sec: parseFloat(m3Sec.toFixed(4)),
            flowVelocity: parseFloat(flowVelocity.toFixed(2)),
            exposureTime: parseFloat(exposureTime.toFixed(4)),
            at1mWcm2,
            at1Wcm2: parseFloat(at1Wcm2.toFixed(4)),
            at1mIReqD90Wm2: parseFloat(at1mIReqD90Wm2.toFixed(2)),
            at1mNoOfLampsD90: parseFloat(at1mNoOfLampsD90.toFixed(2)),
            at1mIReqD99Wm2: parseFloat(at1mIReqD99Wm2.toFixed(2)),
            at1mNoOfLampsD99: parseFloat(at1mNoOfLampsD99.toFixed(2)),
            at450mWcm2,
            at450mmWm2: parseFloat(at450mmWm2.toFixed(4)),
            iReqD90Wm2: parseFloat(iReqD90Wm2.toFixed(2)),
            noOfLampsD90: parseFloat(noOfLampsD90.toFixed(2)),
            iReqD99Wm2: parseFloat(iReqD99Wm2.toFixed(2)),
            noOfLampsD99: parseFloat(noOfLampsD99.toFixed(2)),
            correctedUVD: parseInt(correctedDosage.toFixed(0)),
            minimumIntAtD: parseInt(minIntensity.toFixed(0)),
            intensityAtA: parseInt(aIntensity.toFixed(0)),
            intensityAtB: parseInt(bIntensity.toFixed(0))
        });
    };

    // AHU Calculator Functions
    const handleAhuCalculation = () => {
        const airflowVolCfm = parseFloat(ahuData.airflowVolCfm);
        const coilWMm = parseFloat(ahuData.coilWMm);
        const coilHMm = parseFloat(ahuData.coilHMm);
        const uvLampLMm = parseFloat(ahuData.uvLampLMm);
        const uvWattageW = parseFloat(ahuData.uvWattageW);
        const distance = parseFloat(ahuData.distance);
        const numRows = parseFloat(ahuData.numRows);
        const totalWattage = parseFloat(ahuData.totalWattage);
        const rowWattage = parseFloat(ahuData.rowWattage);
        const noOfLamps = parseFloat(ahuData.noOfLamps);
        const distBtwnLamp = parseFloat(ahuData.distBtwnLamp);

        // Basic conversions
        const uvLampLInch = uvLampLMm / 25.4;
        const uvLampLCm = uvLampLMm / 10;
        const t2LA = 6.28 * uvLampLCm * distance;
        const eWcm2 = uvWattageW / t2LA;
        const eMWcm2 = eWcm2 * 1000000;

        // Exposure time calculation
        const distanceInM = distance * 0.01;
        const coilAreaMm2 = coilWMm * coilHMm;
        const coilAreaM2 = coilAreaMm2 / 1000000;
        const valFlowRateM3S = airflowVolCfm * 0.000471947;
        const flowVelMs = valFlowRateM3S / coilAreaM2;
        const exposureTimeTs = distanceInM / flowVelMs;

        const doseEt = eMWcm2 * exposureTimeTs;
        const totalIntensityMWcm2 = noOfLamps * eMWcm2;
        const doseJm2 = doseEt * 0.01;
        const noOfLampsCalc = coilHMm / distBtwnLamp;

        // Complex calculations for final results
        const lengthInCm = coilHMm / 10;
        const widthInCm = coilWMm / 10;
        const uvLampLMm1 = uvLampLMm / 10;

        const totalAverageIntensity = ((totalWattage * 0.3) * (Math.log(distance / 2) + 0.577) * (10 ** 6)) / (2 * Math.PI * widthInCm * (distance / 2));
        const contactTime = (((distance / 30.48) * 1.5 * (lengthInCm / 30.48) * (widthInCm / 30.48)) / airflowVolCfm) * 60;
        const correctedDosage = totalAverageIntensity * contactTime * 0.7;
        const correctedUVDx001 = correctedDosage * 0.01;

        const diagonalDistance = Math.sqrt((lengthInCm / (numRows * 2)) ** 2 + distance ** 2);
        const minIntensity = ((rowWattage * 0.3 * 10 ** 6) / (2 * Math.PI * uvLampLMm1 * diagonalDistance));
        let maxIntensity = ((rowWattage * 0.3 * 10 ** 6) / (2 * Math.PI * uvLampLMm1 * distance));

        if (numRows >= 3) {
            maxIntensity += minIntensity * 2;
        }

        const avgIntensity = ((minIntensity * 2) + (numRows * maxIntensity) + (numRows - 1) * (minIntensity * 2)) / (numRows * 2 + 1);

        setAhuCalculations({
            uvLampLInch: parseFloat(uvLampLInch.toFixed(2)),
            uvLampLCm: parseFloat(uvLampLCm.toFixed(2)),
            t2LA: parseFloat(t2LA.toFixed(2)),
            eWcm2: parseFloat(eWcm2.toFixed(4)),
            eMWcm2: parseFloat(eMWcm2.toFixed(2)),
            exposureTimeTs: parseFloat(exposureTimeTs.toFixed(4)),
            doseEt: parseFloat(doseEt.toFixed(2)),
            totalIntensityMWcm2: parseFloat(totalIntensityMWcm2.toFixed(2)),
            doseJm2: parseFloat(doseJm2.toFixed(3)),
            noOfLamps: parseFloat(noOfLampsCalc.toFixed(2)),
            correctedUVD: parseInt(correctedDosage.toFixed(0)),
            correctedUVDx001: parseFloat(correctedUVDx001.toFixed(2)),
            minimumInt: parseInt(minIntensity.toFixed(0)),
            maximumInt: parseInt(maxIntensity.toFixed(0)),
            avgInt: parseInt(avgIntensity.toFixed(0))
        });
    };





    const generatePDF = (includeCalculations = true) => {
        const isAhu = activeCalculator === 'ahu';
        const data = isAhu ? ahuData : ductData;
        const calculations = isAhu ? ahuCalculations : ductCalculations;

        const pdfContent = `
    <!DOCTYPE html>
        <html lang="en">
        <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>UV Calculation Report</title>
        </head>
        <body style="font-family: Arial, sans-serif; margin: 0; padding: 20px; background-color: #f5f5f5;">
            
            <div style="background-color: white; max-width: 800px; margin: 0 auto; padding: 20px; box-shadow: 0 0 10px rgba(0,0,0,0.1);">
                
                <!-- Header -->
                <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #007cff; padding-bottom: 10px; margin-bottom: 20px;">
                    <span style="font-size: 24px; font-weight: bold; color: #007cff;">${isAhu ? 'AHU-Result' : 'Duct-Result'}</span>
                    <div>
                        <img src="https://ultrapureindia.in/ultrapure_logo.svg" alt="Logo" style="width: 150px;">
                    </div>
                </div>

                <!-- Content -->
                <div>
                    <div style="display: flex; flex-wrap: wrap; gap: 20px;">
                        
                        <!-- Left Column - Input Parameters -->
                        <div style="flex: 1; min-width: 300px;">
                            <div style="margin-bottom: 10px;">
                                <span style="font-weight: bold; color: #333; display: inline-block; min-width: 150px;">Client Name:</span>
                                <span style="color: #666;">${data.clientName || 'N/A'}</span>
                            </div>
                            
                            ${isAhu ? `
                            <div style="margin-bottom: 10px;">
                                <span style="font-weight: bold; color: #333; display: inline-block; min-width: 150px;">Coil Width:</span>
                                <span style="color: #666;">${data.coilWMm || 'N/A'} mm</span>
                            </div>
                            <div style="margin-bottom: 10px;">
                                <span style="font-weight: bold; color: #333; display: inline-block; min-width: 150px;">Coil Height:</span>
                                <span style="color: #666;">${data.coilHMm || 'N/A'} mm</span>
                            </div>
                            <div style="margin-bottom: 10px;">
                                <span style="font-weight: bold; color: #333; display: inline-block; min-width: 150px;">Lamp Length:</span>
                                <span style="color: #666;">${data.uvLampLMm || 'N/A'} mm</span>
                            </div>
                            <div style="margin-bottom: 10px;">
                                <span style="font-weight: bold; color: #333; display: inline-block; min-width: 150px;">Distance:</span>
                                <span style="color: #666;">${data.distance || 'N/A'} cm</span>
                            </div>
                            <div style="margin-bottom: 10px;">
                                <span style="font-weight: bold; color: #333; display: inline-block; min-width: 150px;">Flow Rate:</span>
                                <span style="color: #666;">${data.airflowVolCfm || 'N/A'} cfm</span>
                            </div>
                            <div style="margin-bottom: 10px;">
                                <span style="font-weight: bold; color: #333; display: inline-block; min-width: 150px;">Number of Rows:</span>
                                <span style="color: #666;">${data.numRows || 'N/A'} Nos</span>
                            </div>
                            <div style="margin-bottom: 10px;">
                                <span style="font-weight: bold; color: #333; display: inline-block; min-width: 150px;">Number of Columns:</span>
                                <span style="color: #666;">${data.numCols || 'N/A'} Nos</span>
                            </div>
                            <div style="margin-bottom: 10px;">
                                <span style="font-weight: bold; color: #333; display: inline-block; min-width: 150px;">Total Count:</span>
                                <span style="color: #666;">${data.lampCount || 'N/A'} Nos</span>
                            </div>
                            <div style="margin-bottom: 10px;">
                                <span style="font-weight: bold; color: #333; display: inline-block; min-width: 150px;">Total Lamp Wattage:</span>
                                <span style="color: #666;">${data.totalWattage || 'N/A'} Watts</span>
                            </div>
                            <div style="margin-bottom: 10px;">
                                <span style="font-weight: bold; color: #333; display: inline-block; min-width: 150px;">Row Lamp Wattage:</span>
                                <span style="color: #666;">${data.rowWattage || 'N/A'} Watts</span>
                            </div>
                            ` : `
                            <div style="margin-bottom: 10px;">
                                <span style="font-weight: bold; color: #333; display: inline-block; min-width: 150px;">UV Lamp Size:</span>
                                <span style="color: #666;">${data.uvSize || 'N/A'} mm</span>
                            </div>
                            <div style="margin-bottom: 10px;">
                                <span style="font-weight: bold; color: #333; display: inline-block; min-width: 150px;">Duct Width:</span>
                                <span style="color: #666;">${data.width || 'N/A'} mm</span>
                            </div>
                            <div style="margin-bottom: 10px;">
                                <span style="font-weight: bold; color: #333; display: inline-block; min-width: 150px;">Duct Length:</span>
                                <span style="color: #666;">${data.length || 'N/A'} mm</span>
                            </div>
                            <div style="margin-bottom: 10px;">
                                <span style="font-weight: bold; color: #333; display: inline-block; min-width: 150px;">Flow Rate:</span>
                                <span style="color: #666;">${data.cfm || 'N/A'} cfm</span>
                            </div>
                            <div style="margin-bottom: 10px;">
                                <span style="font-weight: bold; color: #333; display: inline-block; min-width: 150px;">Lamp Length:</span>
                                <span style="color: #666;">${data.lampLength || 'N/A'} mm</span>
                            </div>
                            <div style="margin-bottom: 10px;">
                                <span style="font-weight: bold; color: #333; display: inline-block; min-width: 150px;">Number of Rows:</span>
                                <span style="color: #666;">${data.numRows || 'N/A'} Nos</span>
                            </div>
                            <div style="margin-bottom: 10px;">
                                <span style="font-weight: bold; color: #333; display: inline-block; min-width: 150px;">Number of Columns:</span>
                                <span style="color: #666;">${data.numCols || 'N/A'} Nos</span>
                            </div>
                            <div style="margin-bottom: 10px;">
                                <span style="font-weight: bold; color: #333; display: inline-block; min-width: 150px;">Total Lamp Wattage:</span>
                                <span style="color: #666;">${data.totalWattage || 'N/A'} Watt</span>
                            </div>
                            <div style="margin-bottom: 10px;">
                                <span style="font-weight: bold; color: #333; display: inline-block; min-width: 150px;">UV Intensity at 1m (mW/cm2):</span>
                                <span style="color: #666;">${data.at1mWcm2Input || 'N/A'}</span>
                            </div>
                            <div style="margin-bottom: 10px;">
                                <span style="font-weight: bold; color: #333; display: inline-block; min-width: 150px;">UV Intensity at "x" mm (mW/cm2):</span>
                                <span style="color: #666;">${data.at450mWcm2Input || 'N/A'}</span>
                            </div>
                            `}
                        </div>

                        <!-- Right Column - Detailed Calculations (for AHU only) -->
                        ${includeCalculations && isAhu ? `
                        <div style="flex: 1; min-width: 300px;">
                            <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; border-left: 4px solid #007cff;">
                                <div style="font-size: 18px; font-weight: bold; color: #007cff; margin-bottom: 15px;">Detailed Calculations</div>
                                <div style="margin-bottom: 10px;">
                                    <span style="font-weight: bold; color: #333; display: inline-block; min-width: 180px;">UV Lamp (L) (cm):</span>
                                    <span style="color: #666;">${calculations.uvLampLCm}</span>
                                </div>
                                <div style="margin-bottom: 10px;">
                                    <span style="font-weight: bold; color: #333; display: inline-block; min-width: 180px;">2ΠLA:</span>
                                    <span style="color: #666;">${calculations.t2LA}</span>
                                </div>
                                <div style="margin-bottom: 10px;">
                                    <span style="font-weight: bold; color: #333; display: inline-block; min-width: 180px;">E= Φ/2ΠLA (W/cm2):</span>
                                    <span style="color: #666;">${calculations.eWcm2}</span>
                                </div>
                                <div style="margin-bottom: 10px;">
                                    <span style="font-weight: bold; color: #333; display: inline-block; min-width: 180px;">E (µW/cm2):</span>
                                    <span style="color: #666;">${calculations.eMWcm2}</span>
                                </div>
                                <div style="margin-bottom: 10px;">
                                    <span style="font-weight: bold; color: #333; display: inline-block; min-width: 180px;">Exposure time t (s):</span>
                                    <span style="color: #666;">${calculations.exposureTimeTs}</span>
                                </div>
                                <div style="margin-bottom: 10px;">
                                    <span style="font-weight: bold; color: #333; display: inline-block; min-width: 180px;">Dose= E*t (µW.s/cm2):</span>
                                    <span style="color: #666;">${calculations.doseEt}</span>
                                </div>
                                <div style="margin-bottom: 10px;">
                                    <span style="font-weight: bold; color: #333; display: inline-block; min-width: 180px;">Total intensity (µW/cm2):</span>
                                    <span style="color: #666;">${calculations.totalIntensityMWcm2}</span>
                                </div>
                            </div>
                        </div>
                        ` : ''}
                    </div>

                    <!-- Simulation Results -->
                    <div style="background-color: #e8f4fd; padding: 20px; margin-top: 30px; border-radius: 8px; border: 2px solid #007cff;">
                        <div style="font-size: 20px; font-weight: bold; color: #007cff; margin-bottom: 15px;">Simulation Result</div>
                        <div style="margin-bottom: 15px; font-size: 16px;">
                            Your Corrected UV Dosage is <strong style="color: #007cff;">${calculations.correctedUVD} microwatt/ sq.cm./sec</strong>.
                        </div>
                        ${isAhu ? `
                        <div style="margin-bottom: 15px; font-size: 16px;">
                            Your Corrected UV Dosage in J/m2 <strong style="color: #007cff;">${calculations.correctedUVDx001}</strong>.
                        </div>
                        <div style="margin-bottom: 15px; font-size: 16px;">
                            Your Minimum Intensity is <strong style="color: #007cff;">${calculations.minimumInt} microwatt/ sq.cm</strong>.
                        </div>
                        <div style="margin-bottom: 15px; font-size: 16px;">
                            Your Maximum Intensity is <strong style="color: #007cff;">${calculations.maximumInt} microwatt/ sq.cm</strong>.
                        </div>
                        <div style="margin-bottom: 15px; font-size: 16px;">
                            Your Average Intensity is <strong style="color: #007cff;">${calculations.avgInt} microwatt/ sq.cm</strong>.
                        </div>
                        ` : `
                        <div style="margin-bottom: 15px; font-size: 16px;">
                            Your Corrected UV Dosage in J/m2 is <strong style="color: #007cff;">${(calculations.correctedUVD * 0.01).toFixed(2)}</strong>.
                        </div>
                        <div style="margin-bottom: 15px; font-size: 16px;">
                            Your Minimum Intensity at "d" is <strong style="color: #007cff;">${calculations.minimumIntAtD} microwatt/ sq.cm</strong>.
                        </div>
                        <div style="margin-bottom: 15px; font-size: 16px;">
                            Your Intensity at "a" is <strong style="color: #007cff;">${calculations.intensityAtA} microwatt/ sq.cm</strong>.
                        </div>
                        <div style="margin-bottom: 15px; font-size: 16px;">
                            Your Intensity at "b" is <strong style="color: #007cff;">${calculations.intensityAtB} microwatt/ sq.cm</strong>.
                        </div>
                        `}
                    </div>
                </div>

                <!-- Footer -->
                <div style="text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; font-size: 12px; color: #666; line-height: 1.6;">
                    <div style="font-weight: bold; font-size: 14px; color: #007cff; margin-bottom: 10px;">
                        ULTRAPURE ENVIROCARE PRIVATE LIMITED<br>
                        (FORMERLY ULTRAFRESH MARKETING PVT LTD)
                    </div>
                    <div style="margin-bottom: 10px;">
                        <strong>CIN: U51909DL2003PTC120170</strong>
                    </div>
                    <div>
                        <strong>Registered Office:</strong> CB-344, 2nd Floor, Ring road, Naraina, New Delhi – 110028,<br>
                        <strong>Email:</strong> <span style="color: #007cff;">info@ultrapureindia.in</span> |
                        <strong>Website:</strong> <span style="color: #007cff;">www.ultrapureindia.in</span> |
                        <strong>Toll Free No.:</strong> 1800 11 00 50.<br>
                        <strong>Telephone:</strong> +91 7428534141.<br>
                        <strong>Branch Locations:</strong> Delhi | Kolkata | Nagpur | Ahmedabad | Bangluru | Chennai | Pune | Hyderabad.
                    </div>
                </div>
            </div>

        </body>
        </html>
  `;

        // Create a hidden container for rendering
        const element = document.createElement("div");
        element.innerHTML = pdfContent;
        document.body.appendChild(element);

        // Use html2pdf
        html2pdf()
            .set({
                margin: 10,
                filename: `${isAhu ? 'AHU' : 'Duct'}-Calculations${includeCalculations ? '' : '-Simple'}.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2 },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
            })
            .from(element)
            .save()
            .then(() => {
                document.body.removeChild(element); // Clean up
            });
    };


    return (
        <div className="min-h-screen bg-gray-100 p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-4">
                        {activeCalculator === 'duct' ? 'Duct Calculator' : 'AHU Calculator'}
                    </h1>
                    <div className="flex justify-center space-x-4">
                        <button
                            onClick={() => setActiveCalculator('duct')}
                            className={`px-6 py-2 rounded-lg font-medium ${activeCalculator === 'duct'
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                        >
                            {/* <Calculator className="inline-block w-4 h-4 mr-2" /> */}
                            Duct Calculator
                        </button>
                        <button
                            onClick={() => setActiveCalculator('ahu')}
                            className={`px-6 py-2 rounded-lg font-medium ${activeCalculator === 'ahu'
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                        >
                            {/* <Zap className="inline-block w-4 h-4 mr-2" /> */}
                            AHU Calculator
                        </button>
                    </div>
                </div>

                {/* Duct Calculator */}
                {activeCalculator === 'duct' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Input Section */}
                        <div className="bg-white p-6 rounded-lg shadow-lg">
                            <h3 className="text-xl font-semibold text-blue-600 mb-4">User Input</h3>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Client Name</label>
                                    <input
                                        type="text"
                                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                        value={ductData.clientName}
                                        onChange={(e) => setDuctData({ ...ductData, clientName: e.target.value })}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">UV Lamp Size (mm)</label>
                                    <input
                                        type="number"
                                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                        value={ductData.uvSize}
                                        onChange={(e) => setDuctData({ ...ductData, uvSize: e.target.value })}
                                    />
                                    {ductData.uvSize && (
                                        <p className="text-sm text-gray-500 mt-1">in cm: {mmToCm(ductData.uvSize).toFixed(2)}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Duct Width (mm)</label>
                                    <input
                                        type="number"
                                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                        value={ductData.width}
                                        onChange={(e) => setDuctData({ ...ductData, width: e.target.value })}
                                    />
                                    {ductData.width && (
                                        <p className="text-sm text-gray-500 mt-1">in cm: {mmToCm(ductData.width).toFixed(2)}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Duct Length (mm)</label>
                                    <input
                                        type="number"
                                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                        value={ductData.length}
                                        onChange={(e) => setDuctData({ ...ductData, length: e.target.value })}
                                    />
                                    {ductData.length && (
                                        <p className="text-sm text-gray-500 mt-1">in cm: {mmToCm(ductData.length).toFixed(2)}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">CFM</label>
                                    <input
                                        type="number"
                                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                        value={ductData.cfm}
                                        onChange={(e) => setDuctData({ ...ductData, cfm: e.target.value })}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Lamp Length (mm)</label>
                                    <input
                                        type="number"
                                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                        value={ductData.lampLength}
                                        onChange={(e) => setDuctData({ ...ductData, lampLength: e.target.value })}
                                    />
                                    {ductData.lampLength && (
                                        <p className="text-sm text-gray-500 mt-1">in cm: {mmToCm(ductData.lampLength).toFixed(2)}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Number of Rows</label>
                                    <input
                                        type="number"
                                        placeholder="of lamps"
                                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                        value={ductData.numRows}
                                        onChange={(e) => setDuctData({ ...ductData, numRows: e.target.value })}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Number of Columns</label>
                                    <input
                                        type="number"
                                        placeholder="of lamps"
                                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                        value={ductData.numCols}
                                        onChange={(e) => setDuctData({ ...ductData, numCols: e.target.value })}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Total Lamp Wattage (W)</label>
                                    <input
                                        type="number"
                                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                        value={ductData.totalWattage}
                                        onChange={(e) => setDuctData({ ...ductData, totalWattage: e.target.value })}
                                    />
                                </div>

                                <div className="border-t pt-4">
                                    <h4 className="font-medium text-gray-900 mb-2">Intensity of UV lamp:</h4>
                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">at 1 meter (mW/cm²)</label>
                                            <input
                                                type="number"
                                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                                value={ductData.at1mWcm2Input}
                                                onChange={(e) => setDuctData({ ...ductData, at1mWcm2Input: e.target.value })}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">at "x" mm (mW/cm²)</label>
                                            <input
                                                type="number"
                                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                                value={ductData.at450mWcm2Input}
                                                onChange={(e) => setDuctData({ ...ductData, at450mWcm2Input: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="flex space-x-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setDuctData({
                                                clientName: '',
                                                uvSize: '',
                                                width: '',
                                                length: '',
                                                cfm: '',
                                                lampLength: '',
                                                numRows: '',
                                                numCols: '',
                                                totalWattage: '',
                                                at1mWcm2Input: '',
                                                at450mWcm2Input: ''
                                            });
                                            setDuctCalculations({
                                                ductAreaMm2: 0,
                                                ductAreaM2: 0,
                                                m3Hr: 0,
                                                m3Min: 0,
                                                m3Sec: 0,
                                                flowVelocity: 0,
                                                exposureTime: 0,
                                                at1mWcm2: 0,
                                                at1Wcm2: 0,
                                                at1mIReqD90Wm2: 0,
                                                at1mNoOfLampsD90: 0,
                                                at1mIReqD99Wm2: 0,
                                                at1mNoOfLampsD99: 0,
                                                at450mWcm2: 0,
                                                at450mmWm2: 0,
                                                iReqD90Wm2: 0,
                                                noOfLampsD90: 0,
                                                iReqD99Wm2: 0,
                                                noOfLampsD99: 0,
                                                correctedUVD: 0,
                                                minimumIntAtD: 0,
                                                intensityAtA: 0,
                                                intensityAtB: 0
                                            });
                                        }}
                                        className="flex-1 px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                                    >
                                        Reset
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleDuctCalculation}
                                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                    >
                                        Calculate
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Calculations 1 */}
                        <div className="bg-white p-6 rounded-lg shadow-lg">
                            <h3 className="text-xl font-semibold text-green-600 mb-4">Calculations: <span className="text-red-600">1</span></h3>
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Duct Area (mm²)</label>
                                        <input
                                            type="text"
                                            className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                                            value={ductCalculations.ductAreaMm2}
                                            readOnly
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Duct Area (m²)</label>
                                        <input
                                            type="text"
                                            className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                                            value={ductCalculations.ductAreaM2}
                                            readOnly
                                        />
                                    </div>
                                </div>

                                <div className="border-t pt-4">
                                    <h4 className="font-medium text-gray-900 mb-2">Vol. Flow Rate:</h4>
                                    <div className="grid grid-cols-3 gap-2">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">m³/hr</label>
                                            <input
                                                type="text"
                                                className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md text-sm"
                                                value={ductCalculations.m3Hr}
                                                readOnly
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">m³/min</label>
                                            <input
                                                type="text"
                                                className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md text-sm"
                                                value={ductCalculations.m3Min}
                                                readOnly
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">m³/sec</label>
                                            <input
                                                type="text"
                                                className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md text-sm"
                                                value={ductCalculations.m3Sec}
                                                readOnly
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Flow Velocity (m/s)</label>
                                        <input
                                            type="text"
                                            className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                                            value={ductCalculations.flowVelocity}
                                            readOnly
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Exposure Time (s)</label>
                                        <input
                                            type="text"
                                            className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                                            value={ductCalculations.exposureTime}
                                            readOnly
                                        />
                                    </div>
                                </div>

                                <div className="border-t pt-4">
                                    <h4 className="font-medium text-gray-900 mb-2 underline">at 1 meter</h4>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">mW/cm²</label>
                                            <input
                                                type="text"
                                                className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                                                value={ductCalculations.at1mWcm2}
                                                readOnly
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">W/m²</label>
                                            <input
                                                type="text"
                                                className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                                                value={ductCalculations.at1Wcm2}
                                                readOnly
                                            />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4 mt-2">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">I req. (D90) [W/m²]</label>
                                            <input
                                                type="text"
                                                className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                                                value={ductCalculations.at1mIReqD90Wm2}
                                                readOnly
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">No. of Lamps</label>
                                            <input
                                                type="text"
                                                className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                                                value={ductCalculations.at1mNoOfLampsD90}
                                                readOnly
                                            />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4 mt-2">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">I req. (D99) [W/m²]</label>
                                            <input
                                                type="text"
                                                className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                                                value={ductCalculations.at1mIReqD99Wm2}
                                                readOnly
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">No. of Lamps</label>
                                            <input
                                                type="text"
                                                className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                                                value={ductCalculations.at1mNoOfLampsD99}
                                                readOnly
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="border-t pt-4">
                                    <h4 className="font-medium text-gray-900 mb-2 underline">at "x" mm</h4>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">mW/cm²</label>
                                            <input
                                                type="text"
                                                className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                                                value={ductCalculations.at450mWcm2}
                                                readOnly
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">W/m²</label>
                                            <input
                                                type="text"
                                                className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                                                value={ductCalculations.at450mmWm2}
                                                readOnly
                                            />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4 mt-2">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">I req. (D90) [W/m²]</label>
                                            <input
                                                type="text"
                                                className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                                                value={ductCalculations.iReqD90Wm2}
                                                readOnly
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">No. of Lamps</label>
                                            <input
                                                type="text"
                                                className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                                                value={ductCalculations.noOfLampsD90}
                                                readOnly
                                            />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4 mt-2">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">I req. (D99) [W/m²]</label>
                                            <input
                                                type="text"
                                                className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                                                value={ductCalculations.iReqD99Wm2}
                                                readOnly
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">No. of Lamps</label>
                                            <input
                                                type="text"
                                                className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                                                value={ductCalculations.noOfLampsD99}
                                                readOnly
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Calculations 2 */}
                        <div className="bg-white p-6 rounded-lg shadow-lg">
                            <h3 className="text-xl font-semibold text-green-600 mb-4">Calculations: <span className="text-red-600">2</span></h3>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Corrected UV Dosage <span className="text-gray-500">(microwatt/sq.cm/sec)</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                                        value={ductCalculations.correctedUVD}
                                        readOnly
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Minimum Intensity at 'd' <span className="text-gray-500">(microwatt/sq.cm)</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                                        value={ductCalculations.minimumIntAtD}
                                        readOnly
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Intensity at 'a' <span className="text-gray-500">(microwatt/sq.cm)</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                                        value={ductCalculations.intensityAtA}
                                        readOnly
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Intensity at 'b' <span className="text-gray-500">(microwatt/sq.cm)</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                                        value={ductCalculations.intensityAtB}
                                        readOnly
                                    />
                                </div>
                            </div>

                            <div className="mt-6 border-t pt-4">
                                <h4 className="text-sm font-medium text-gray-700 mb-3">PDF Download</h4>
                                <div className="space-y-3">
                                    <div className="flex items-center space-x-2">
                                        <input
                                            type="radio"
                                            id="all-duct"
                                            name="duct-pdf"
                                            className="text-blue-600"
                                            onChange={() => generatePDF(true)}
                                        />
                                        <label htmlFor="all-duct" className="text-sm text-gray-700 cursor-pointer">
                                            All (with detailed calculations)
                                        </label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <input
                                            type="radio"
                                            id="simple-duct"
                                            name="duct-pdf"
                                            className="text-blue-600"
                                            onChange={() => generatePDF(false)}
                                        />
                                        <label htmlFor="simple-duct" className="text-sm text-gray-700 cursor-pointer">
                                            Without Calculation 1
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* AHU Calculator */}
                {activeCalculator === 'ahu' && (
                    <div className="min-h-screen bg-gray-100">
                        <div className="max-w-7xl mx-auto">

                            {/* AHU Calculator */}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                {/* Input Section */}
                                <div className="bg-white p-6 rounded-lg shadow-lg">
                                    <h3 className="text-xl font-semibold text-blue-600 mb-4">User Input</h3>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Client Name</label>
                                            <input
                                                type="text"
                                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                                value={ahuData.clientName}
                                                onChange={(e) => setAhuData({ ...ahuData, clientName: e.target.value })}
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Coil (W) [mm]</label>
                                            <input
                                                type="number"
                                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                                value={ahuData.coilWMm}
                                                onChange={(e) => setAhuData({ ...ahuData, coilWMm: e.target.value })}
                                            />
                                            {ahuData.coilWMm && (
                                                <p className="text-sm text-gray-500 mt-1">in cm: {mmToCm(ahuData.coilWMm).toFixed(1)}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Coil (H) [mm]</label>
                                            <input
                                                type="number"
                                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                                value={ahuData.coilHMm}
                                                onChange={(e) => setAhuData({ ...ahuData, coilHMm: e.target.value })}
                                            />
                                            {ahuData.coilHMm && (
                                                <p className="text-sm text-gray-500 mt-1">in cm: {mmToCm(ahuData.coilHMm).toFixed(1)}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">UV LAMP (L) [mm]</label>
                                            <input
                                                type="number"
                                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                                value={ahuData.uvLampLMm}
                                                onChange={(e) => setAhuData({ ...ahuData, uvLampLMm: e.target.value })}
                                            />
                                            {ahuData.uvLampLMm && (
                                                <p className="text-sm text-gray-500 mt-1">in cm: {mmToCm(ahuData.uvLampLMm).toFixed(1)}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Lamp Coil Distance (cm)</label>
                                            <input
                                                type="number"
                                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                                value={ahuData.distance}
                                                onChange={(e) => setAhuData({ ...ahuData, distance: e.target.value })}
                                            />
                                            {ahuData.distance && (
                                                <p className="text-sm text-gray-500 mt-1">in m: {cmToM(ahuData.distance).toFixed(2)}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Airflow Vol. (CFM)</label>
                                            <input
                                                type="number"
                                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                                value={ahuData.airflowVolCfm}
                                                onChange={(e) => setAhuData({ ...ahuData, airflowVolCfm: e.target.value })}
                                            />
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Rows</label>
                                                <input
                                                    type="number"
                                                    placeholder="of lamps"
                                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                                    value={ahuData.numRows}
                                                    onChange={(e) => setAhuData({ ...ahuData, numRows: e.target.value })}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Columns</label>
                                                <input
                                                    type="number"
                                                    placeholder="of lamps"
                                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                                    value={ahuData.numCols}
                                                    onChange={(e) => setAhuData({ ...ahuData, numCols: e.target.value })}
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Total Count</label>
                                            <input
                                                type="number"
                                                placeholder="of lamps"
                                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                                value={ahuData.lampCount}
                                                onChange={(e) => setAhuData({ ...ahuData, lampCount: e.target.value })}
                                            />
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Total Lamp Wattage (W)</label>
                                                <input
                                                    type="number"
                                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                                    value={ahuData.totalWattage}
                                                    onChange={(e) => setAhuData({ ...ahuData, totalWattage: e.target.value })}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Row Lamp Wattage (W)</label>
                                                <input
                                                    type="number"
                                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                                    value={ahuData.rowWattage}
                                                    onChange={(e) => setAhuData({ ...ahuData, rowWattage: e.target.value })}
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Intensity at 10 inch (µW/cm²)</label>
                                            <input
                                                type="number"
                                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                                value={ahuData.intensityAt1m}
                                                onChange={(e) => setAhuData({ ...ahuData, intensityAt1m: e.target.value })}
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">UV wattage (W), Φ</label>
                                            <input
                                                type="number"
                                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                                value={ahuData.uvWattageW}
                                                onChange={(e) => setAhuData({ ...ahuData, uvWattageW: e.target.value })}
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">No. of lamps</label>
                                            <input
                                                type="number"
                                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                                value={ahuData.noOfLamps}
                                                onChange={(e) => setAhuData({ ...ahuData, noOfLamps: e.target.value })}
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Distance between Lamp (mm)</label>
                                            <input
                                                type="number"
                                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                                value={ahuData.distBtwnLamp}
                                                onChange={(e) => setAhuData({ ...ahuData, distBtwnLamp: e.target.value })}
                                            />
                                        </div>

                                        <div className="flex space-x-3 pt-4">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setAhuData({
                                                        clientName: '',
                                                        coilWMm: '',
                                                        coilHMm: '',
                                                        uvLampLMm: '',
                                                        distance: '',
                                                        airflowVolCfm: '',
                                                        numRows: '',
                                                        numCols: '',
                                                        lampCount: '',
                                                        totalWattage: '',
                                                        rowWattage: '',
                                                        intensityAt1m: '',
                                                        uvWattageW: '',
                                                        noOfLamps: '',
                                                        distBtwnLamp: ''
                                                    });
                                                    setAhuCalculations({
                                                        uvLampLInch: 0,
                                                        uvLampLCm: 0,
                                                        t2LA: 0,
                                                        eWcm2: 0,
                                                        eMWcm2: 0,
                                                        exposureTimeTs: 0,
                                                        doseEt: 0,
                                                        totalIntensityMWcm2: 0,
                                                        doseJm2: 0,
                                                        noOfLamps: 0,
                                                        correctedUVD: 0,
                                                        correctedUVDx001: 0,
                                                        minimumInt: 0,
                                                        maximumInt: 0,
                                                        avgInt: 0
                                                    });
                                                }}
                                                className="flex-1 px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                                            >
                                                Reset
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleAhuCalculation}
                                                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                            >
                                                Calculate
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Calculations 1 */}
                                <div className="bg-white p-6 rounded-lg shadow-lg">
                                    <h3 className="text-xl font-semibold text-green-600 mb-4">Calculations: <span className="text-red-600">1</span></h3>
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">UV LAMP (L) (inch)</label>
                                                <input
                                                    type="text"
                                                    className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                                                    value={ahuCalculations.uvLampLInch}
                                                    readOnly
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">UV Lamp (L) (cm)</label>
                                                <input
                                                    type="text"
                                                    className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                                                    value={ahuCalculations.uvLampLCm}
                                                    readOnly
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">2ΠLA</label>
                                                <input
                                                    type="text"
                                                    className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                                                    value={ahuCalculations.t2LA}
                                                    readOnly
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">E= Φ/2ΠLA (W/cm²)</label>
                                                <input
                                                    type="text"
                                                    className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                                                    value={ahuCalculations.eWcm2}
                                                    readOnly
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">E (µW/cm²)</label>
                                                <input
                                                    type="text"
                                                    className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                                                    value={ahuCalculations.eMWcm2}
                                                    readOnly
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Exposure time t (s)</label>
                                                <input
                                                    type="text"
                                                    className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                                                    value={ahuCalculations.exposureTimeTs}
                                                    readOnly
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Dose= E*t (µW.s/cm²)</label>
                                                <input
                                                    type="text"
                                                    className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                                                    value={ahuCalculations.doseEt}
                                                    readOnly
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Total intensity (µW/cm²)</label>
                                                <input
                                                    type="text"
                                                    className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                                                    value={ahuCalculations.totalIntensityMWcm2}
                                                    readOnly
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Dose (J/m²)</label>
                                                <input
                                                    type="text"
                                                    className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                                                    value={ahuCalculations.doseJm2}
                                                    readOnly
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">No.of Lamps</label>
                                                <input
                                                    type="text"
                                                    className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                                                    value={ahuCalculations.noOfLamps}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Calculations 2 */}
                                <div className="bg-white p-6 rounded-lg shadow-lg">
                                    <h3 className="text-xl font-semibold text-green-600 mb-4">Calculations: <span className="text-red-600">2</span></h3>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Corrected UV Dosage <span className="text-gray-500">(microwatt/sq.cm/sec)</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                                                value={ahuCalculations.correctedUVD}
                                                readOnly
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Corrected UV Dosage X 0.01 <span className="text-gray-500">(J / Sq. meters)</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                                                value={ahuCalculations.correctedUVDx001}
                                                readOnly
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Minimum Intensity <span className="text-gray-500">(microwatt/sq.cm)</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                                                value={ahuCalculations.minimumInt}
                                                readOnly
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Maximum Intensity <span className="text-gray-500">(microwatt/sq.cm)</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                                                value={ahuCalculations.maximumInt}
                                                readOnly
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Average Intensity <span className="text-gray-500">(microwatt/sq.cm)</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md"
                                                value={ahuCalculations.avgInt}
                                                readOnly
                                            />
                                        </div>
                                    </div>

                                    <div className="mt-6 border-t pt-4">
                                        <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                                            {/* <Download className="mr-2" size={16} /> */}
                                            PDF Download
                                        </h4>
                                        <div className="space-y-3">
                                            <div className="flex items-center space-x-2">
                                                <input
                                                    type="radio"
                                                    id="all-ahu"
                                                    name="ahu-pdf"
                                                    className="text-blue-600"
                                                    onChange={() => generatePDF(true)}
                                                />
                                                <label htmlFor="all-ahu" className="text-sm text-gray-700 cursor-pointer">
                                                    All (with detailed calculations)
                                                </label>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <input
                                                    type="radio"
                                                    id="simple-ahu"
                                                    name="ahu-pdf"
                                                    className="text-blue-600"
                                                    onChange={() => generatePDF(false)}
                                                />
                                                <label htmlFor="simple-ahu" className="text-sm text-gray-700 cursor-pointer">
                                                    Without Calculation 1
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default UVCalculatorApp;